import React, { useState, useCallback, useRef, useEffect } from "react";
import { images } from "../../config/images";

const MapComponent = ({
  isSolarFullScreen,
  isFullscreen,
  data,
  type,
  UIDSearchData,
}) => {
  const defaultCenter = { lat: 20.0, lng: 78.0 };
  const defaultZoom = 5;
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const mapRef = useRef(null);
  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(defaultZoom);
  const markersRef = useRef([]);
  const [polyline, setPolyline] = useState(null);

  const mapContainer = (
    <div
      id="map"
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "8px",
      }}
    />
  );

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://apis.mappls.com/advancedmaps/api/b549c76376745c9166019ff7da79a72d/map_sdk?layer=vector&v=3.0";
    script.async = true;
    script.onload = () => {
      if (window.mappls) {
        setIsScriptLoaded(true);
      } else {
        console.error("Mappls SDK not loaded properly");
      }
    };
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (!isScriptLoaded) return;

    const mapContainer = document.getElementById("map");
    if (!mapContainer) {
      console.error("Map container not found");
      return;
    }

    try {
      const map = new window.mappls.Map("map", {
        center: [defaultCenter.lat, defaultCenter.lng],
        zoom: defaultZoom,
        zoomControl: true, // Enable zoom control
        hybrid: true,
      });

      map.on("load", () => {
        mapRef.current = map;
        setIsLoaded(true);
        setTimeout(() => map.resize(), 100);
      });
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }, [isScriptLoaded, defaultCenter.lat, defaultCenter.lng, defaultZoom]);

  const drawPolyline = useCallback((coordinates) => {
    if (!mapRef.current || !coordinates || coordinates.length < 2) return;

    // Clear existing polyline
    if (polyline) {
      try {
        polyline.remove();
      } catch (error) {
        console.error("Error removing polyline:", error);
      }
    }

    // Convert coordinates to Mappls format
    const path = coordinates.map((coord) => [
      parseFloat(coord.latitude),
      parseFloat(coord.longitude),
    ]);

    try {
      // Create new polyline
      const newPolyline = new window.mappls.Polyline({
        map: mapRef.current,
        strokeColor: "#FF0000",
        strokeOpacity: 1.0,
        strokeWeight: 2,
        strokeDashArray: [10, 10],
        path: path,
      });

      setPolyline(newPolyline);

      // Calculate bounds properly
      const bounds = new window.mappls.LatLngBounds();
      path.forEach((point) => {
        bounds.extend(new window.mappls.LatLng(point[0], point[1]));
      });

      // Add some padding and fit to bounds
      mapRef.current.fitBounds(bounds, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 },
        maxZoom: 15, // Optional: set a max zoom level
      });
    } catch (error) {
      console.error("Error creating polyline:", error);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    clearMarkers();
    if (polyline) {
      polyline.setMap(null);
    }

    if (UIDSearchData?.stations?.length > 0) {
      const bounds = new window.mappls.LatLngBounds();
      const coordinates = [];
      UIDSearchData.stations.forEach((station, index) => {
        if (isValidLatLng(station.latitude, station.longitude)) {
          // Determine icon URL based on status
          let iconUrl;
          switch (station.status) {
            case "counterfeit":
              iconUrl = images.redMark;
              break;
            case "cross selling":
              iconUrl = images.orageIcon;
              break;
            case "genuine":
              iconUrl = images.greenMark;
              break;
            default:
              iconUrl = images.mapFactoryIcon;
          }

          // Create marker with Mappls syntax
          const marker = new window.mappls.Marker({
            map: mapRef.current,
            position: [
              parseFloat(station.latitude),
              parseFloat(station.longitude),
            ],
            icon: iconUrl, // Directly pass the URL string
            ...(station.status === "cross selling" ||
            station.status === "genuine" ||
            station.status === "counterfeit"
              ? { offset: [12, 12] }
              : {}),

            draggable: false,
            popupHtml: `
              <div style="
                padding: 8px;
                background: white;
                border-radius: 4px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              ">
                <strong>${station.status}</strong><br>
                Lat: ${station.latitude}<br>
                Lng: ${station.longitude}
              </div>
            `,
          });

          marker.addListener("click", () => handleMarkerClick(station));
          markersRef.current.push(marker);
          // bounds.extend([
          //   parseFloat(station.latitude),
          //   parseFloat(station.longitude),
          // ]);

          coordinates.push({
            latitude: station.latitude,
            longitude: station.longitude,
          });
        }
      });

      // if (markersRef.current.length > 0) {
      //   mapRef.current.fitBounds(bounds);
      // }

      if (coordinates.length > 1) {
        drawPolyline(coordinates);
      }
    } else {
      if (data?.scan_results) {
        data.scan_results
          .filter((location) =>
            isValidLatLng(location.latitude, location.longitude)
          )
          .forEach((location) => {
            if (location.counterfeit_count > 0) {
              const marker = new window.mappls.Marker({
                map: mapRef.current,
                position: [
                  parseFloat(location.latitude),
                  parseFloat(location.longitude),
                ],
                icon: images.redMark,
                draggable: false,
              });
              marker.addListener("click", () => handleMarkerClick(location));
              markersRef.current.push(marker);
            }
            if (location.genuine_count > 0) {
              const marker = new window.mappls.Marker({
                map: mapRef.current,
                position: [
                  parseFloat(location.latitude),
                  parseFloat(location.longitude),
                ],
                icon: images.greenMark,
                draggable: false,
              });
              marker.addListener("click", () => handleMarkerClick(location));
              markersRef.current.push(marker);
            }
            if (location.cross_selling_count > 0) {
              const marker = new window.mappls.Marker({
                map: mapRef.current,
                position: [
                  parseFloat(location.latitude),
                  parseFloat(location.longitude),
                ],
                icon: images.orageIcon,
                draggable: false,
              });
              marker.addListener("click", () => handleMarkerClick(location));
              markersRef.current.push(marker);
            }
          });
      }

      if (data?.supply_chain_data) {
        data.supply_chain_data
          .filter((location) =>
            isValidLatLng(location.latitude, location.longitude)
          )
          .forEach((location) => {
            const icon =
              location.supply_chain === "distributor"
                ? images.mapDistributorIcon
                : location.supply_chain === "plants"
                ? images.mapFactoryIcon
                : images.defaultSupplyIcon;

            const marker = new window.mappls.Marker({
              map: mapRef.current,
              position: [
                parseFloat(location.latitude) + 0.01,
                parseFloat(location.longitude),
              ],
              icon: {
                url: icon,
              },
              draggable: false,
            });
            marker.addListener("click", () => handleMarkerClick(location));
            markersRef.current.push(marker);
          });
      }
    }
  }, [isLoaded, UIDSearchData, data, drawPolyline]);

  const isValidLatLng = (lat, lng) =>
    !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng)) && lat >= -90 && lat <= 90;

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    setCenter({
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude),
    });
    setZoom(12);
  };

  const clearMarkers = () => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
  };

  if (!isLoaded) {
    return (
      <>
        {mapContainer}
        <div className="map-loding-div">Loading Map...</div>
      </>
    );
  }

  return mapContainer;
};

export default MapComponent;
